/**
 * The MEMBER-facing loop DRAFT rpc definitions (Loop Studio).
 *
 * A draft is the ONE working copy of a loop document per (customer, slug) that a
 * person — or their agent, through the MCP — edits before it becomes a version.
 * Writes are whole-document, and every read and write answers with the draft's
 * findings (`documentFindings`, `@fm/loops/analysis/findings`) so the writer
 * sees exactly what the canvas sees. `loops.drafts.publish` runs the same gate
 * `loops.define` / `loops.update` run and mints exactly one version.
 *
 * Contracts only — the handlers live in `loop-drafts.router.ts`.
 *
 * Every wire type is a PROJECTION declared here, the way `LoopListRow`
 * (`loop-definitions.rpc.ts`) is. The two literals it carries — who wrote the
 * draft last, and whether it is open, published or discarded — are the
 * `@fm/db/models/LoopDraft` literals verbatim, never re-declared.
 *
 * `customerId` on a payload is a SELECTOR among the caller's own memberships,
 * never an authority — `resolveActorCustomer` (`loop-scope.ts`) is the only
 * thing that turns a request into a customer scope.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { LoopDocument } from "@fm/db/models/LoopDocument";
import { LoopDraftStatus, LoopDraftWriterVia } from "@fm/db/models/LoopDraft";
import { Schema } from "effect";

import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * One finding on the wire — `DocumentFinding` from `@fm/loops/analysis/findings`,
 * field for field.
 *
 * @since 1.0.0
 */
export const DocumentFindingWire = Schema.Struct({
  severity: Schema.Literal("error", "warning"),
  rule: Schema.String,
  message: Schema.String,
  node: Schema.NullOr(Schema.String),
});
export type DocumentFindingWire = Schema.Schema.Type<typeof DocumentFindingWire>;

/**
 * One draft as every surface renders it: the document, who wrote it last, its
 * findings, and whether it is still open. A closed draft keeps its row; the
 * watch stream's LAST element is the close (`status` `published` or
 * `discarded`), and `published` names the loop and version a publish minted.
 *
 * @since 1.0.0
 */
export const LoopDraftView = Schema.Struct({
  draftId: Schema.String,
  slug: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  /** The existing loop this draft edits; `null` for a new loop. */
  loopId: Schema.NullOr(Schema.String),
  body: LoopDocument,
  revision: Schema.Number,
  updatedAt: Schema.DateTimeUtc,
  lastWriter: Schema.Struct({
    personId: Schema.NullOr(Schema.String),
    via: LoopDraftWriterVia,
  }),
  findings: Schema.Array(DocumentFindingWire),
  status: LoopDraftStatus,
  /** Set only on a draft closed by `loops.drafts.publish`. */
  published: Schema.NullOr(Schema.Struct({ loopId: Schema.String, version: Schema.Number })),
});
export type LoopDraftView = Schema.Schema.Type<typeof LoopDraftView>;

/**
 * A write carried an `expectedRevision` the draft has already moved past.
 *
 * @since 1.0.0
 */
export class LoopDraftConflict extends Schema.TaggedError<LoopDraftConflict>()(
  "LoopDraftConflict",
  {
    draftId: Schema.String,
    currentRevision: Schema.Number,
  },
) {}

/**
 * Open (or re-open) the draft for a slug. Idempotent per slug; `fromLoopId`
 * seeds body, name and description from that loop's head.
 *
 * @since 1.0.0
 */
export const LoopDraftOpenInput = Schema.Struct({
  slug: Schema.String,
  name: Schema.optional(Schema.String),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  fromLoopId: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type LoopDraftOpenInput = Schema.Schema.Type<typeof LoopDraftOpenInput>;

/**
 * One draft addressed by id — `get`, `discard` and the watch stream.
 *
 * @since 1.0.0
 */
export const LoopDraftRefInput = Schema.Struct({
  draftId: Schema.String,
  customerId: Schema.optional(Schema.String),
});
export type LoopDraftRefInput = Schema.Schema.Type<typeof LoopDraftRefInput>;

/**
 * A whole-document write. `expectedRevision` makes it a compare-and-set.
 *
 * @since 1.0.0
 */
export const LoopDraftWriteInput = Schema.Struct({
  draftId: Schema.String,
  body: LoopDocument,
  expectedRevision: Schema.optional(Schema.Number),
  customerId: Schema.optional(Schema.String),
});
export type LoopDraftWriteInput = Schema.Schema.Type<typeof LoopDraftWriteInput>;

/**
 * Turn the draft into one new version. `changeNote` lands on that version row.
 *
 * @since 1.0.0
 */
export const LoopDraftPublishInput = Schema.Struct({
  draftId: Schema.String,
  changeNote: Schema.optional(Schema.NullOr(Schema.String)),
  customerId: Schema.optional(Schema.String),
});
export type LoopDraftPublishInput = Schema.Schema.Type<typeof LoopDraftPublishInput>;

/**
 * The loop the publish wrote and the version it minted.
 *
 * @since 1.0.0
 */
export const LoopDraftPublishResponse = Schema.Struct({
  loopId: Schema.String,
  slug: Schema.String,
  version: Schema.Int,
});
export type LoopDraftPublishResponse = Schema.Schema.Type<typeof LoopDraftPublishResponse>;

/**
 * The member-facing draft group. Every rpc carries {@link LoopActorMiddleware}.
 *
 * @since 1.0.0
 */
export class LoopDraftRpcs extends RpcGroup.make(
  Rpc.make("loops.drafts.open", {
    payload: LoopDraftOpenInput,
    success: LoopDraftView,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.drafts.get", {
    payload: LoopDraftRefInput,
    success: LoopDraftView,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.drafts.write", {
    payload: LoopDraftWriteInput,
    success: LoopDraftView,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      ValidationError,
      DatabaseError,
      LoopDraftConflict,
    ),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.drafts.publish", {
    payload: LoopDraftPublishInput,
    success: LoopDraftPublishResponse,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.drafts.discard", {
    payload: LoopDraftRefInput,
    success: Schema.Void,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
) {}
